import { getUid } from "../../wp_helpers/helpers";

/* ------------------------------------------------------------------------
 * GENERIC — knows nothing about video. Fields cross the boundary as a
 * descriptor `{fieldId, schemaProps, hintProps}`: schemaProps lives in
 * `schema.properties[fieldId]`, hintProps in `propertiesHints[fieldId]`.
 * This is the only code allowed to touch `translations` / `propertiesHints`
 * bookkeeping, and the future CFE delegation point.
 * ---------------------------------------------------------------------- */

/**
 * Assemble a `{schema, propertiesHints, translations}` document from field
 * descriptors. `existingTranslations` is forwarded VERBATIM when given
 * (even `{}` — an explicit "these are the current translations, possibly
 * none"); when omitted (`undefined`), the `translations` key is left off
 * entirely. Sending `translations: {}` unconditionally used to read
 * server-side as "I am the authority on translations and there are none",
 * wiping every per-language title on an ordinary save
 * (`Palaute/utils/video_question_field.py :: replace_video_questions_in_form`,
 * the `has_translations_block` sentinel) — omitting the key instead reads
 * as "no opinion" and leaves them untouched.
 */
const buildSchemaFromFields = (fields = [], existingTranslations) => {
    const schema = { type: 'object', properties: {} };
    const propertiesHints = {};
    fields.forEach(({ fieldId, schemaProps = {}, hintProps = {} }) => {
        schema.properties[fieldId] = schemaProps;
        propertiesHints[fieldId] = hintProps;
    });
    const result = { schema, propertiesHints };
    if (existingTranslations !== undefined) {
        result.translations = existingTranslations;
    }
    return result;
};

/**
 * The inverse of `buildSchemaFromFields`: every field named in
 * `schema.properties` becomes one descriptor. PROPERTIES-authoritative —
 * a `propertiesHints` entry with no matching `schema.properties` entry
 * (an orphan hint) is not a field and does not surface here, mirroring the
 * same decision on the backend
 * (`Palaute/lib/alpaca/form_surgery.py :: extract_fields_subform`).
 */
const parseFieldsFromSchema = (schema = {}) => {
    const properties = schema?.schema?.properties || {};
    const hints = schema?.propertiesHints || {};
    return Object.keys(properties).map((fieldId) => ({
        fieldId,
        schemaProps: properties[fieldId] || {},
        hintProps: hints[fieldId] || {},
    }));
};

/**
 * For every field present in BOTH `oldSchema` and `newSchema`, report
 * whether any of `watchedKeys` differs on `schema.properties[fieldId]`
 * between the two. A field only in `newSchema` (a brand-new field) is not
 * reported — add/remove is a different question this does not answer.
 */
const diffSchemaProperties = (oldSchema, newSchema, watchedKeys = []) => {
    const oldProperties = oldSchema?.schema?.properties || {};
    const newProperties = newSchema?.schema?.properties || {};
    return Object.keys(newProperties)
        .filter((fieldId) => oldProperties[fieldId])
        .map((fieldId) => {
            const oldProps = oldProperties[fieldId] || {};
            const newProps = newProperties[fieldId] || {};
            return {
                fieldId,
                changed: watchedKeys.some((key) => oldProps[key] !== newProps[key]),
            };
        });
};

/* ------------------------------------------------------------------------
 * VIDEO — no knowledge of `properties` / `propertiesHints` / `order`.
 * ---------------------------------------------------------------------- */

const videoQuestionToFieldDescriptor = (field = {}) => {
    const {
        video_question_answerable: answerableRaw,
        video_question_required: required,
        title,
        max_recording_time: mrt,
        video_question: attachmentUrn,
    } = field;
    // Absent marker (rows saved before this flag existed) defaults to answerable.
    const answerable = answerableRaw !== false;
    return {
        schemaProps: {
            // Single-flag semantics: unanswerable can never be mandatory, no matter
            // what the checkbox state says -- the server enforces this too.
            required: answerable ? required : false,
            video_question_answerable: answerable,
            title,
            type: 'string',
            attachment_urn: attachmentUrn,
            video_url: attachmentUrn && `/videos/${attachmentUrn}`,
            thumbnail_url: attachmentUrn && `/videos/${attachmentUrn}/thumb`,
        },
        hintProps: {
            widget: 'video_recorder_widget',
            options: {
                dataSource: '{video_recorder_metadata_url}',
                videoRecorderOptions: {
                    mrt,
                },
            },
        },
    };
};

const fieldDescriptorToVideoQuestion = (schemaProps = {}, hintProps = {}) => {
    const {
        title,
        required,
        video_question_answerable: answerableRaw,
        attachment_urn: attachmentUrn,
        video_url: videoUrl,
        save_to_library_url: saveToLibraryUrl,
    } = schemaProps;
    const { options = {} } = hintProps;
    const mrt = options.videoRecorderOptions?.mrt ?? options.mrt;
    const videoQuestion = attachmentUrn || (videoUrl && videoUrl.replace(/^\/videos\//, ''));
    return {
        title,
        video_question: videoQuestion,
        video_question_answerable: answerableRaw !== false,
        video_question_required: required,
        max_recording_time: mrt,
        // A concrete, server-served URL -- rendered as-is, never built here. Derived
        // and read-only, which is why videoQuestionToFieldDescriptor omits it: it must
        // never round-trip back into the saved form.
        save_to_library_url: saveToLibraryUrl,
    };
};

/**
 * The video-recorder's default URN, wherever a schema-side field descriptor
 * carries it: explicit `attachment_urn`, or derived from `video_url` when
 * that's the only trace of it left. Resolving both to the SAME value
 * before comparing is what makes `isAnsweredVideoQuestionChanged` treat
 * either representation of the same video as unchanged — the frontend
 * analogue of the backend's `resolve_default_video_urn`
 * (`Palaute/utils/video_question_field.py`), which resolves hint-side
 * `video_question` vs schema-side `attachment_urn` before comparing.
 */
const resolveSchemaVideoUrn = (schemaProps = {}) => (
    schemaProps.attachment_urn
    || (schemaProps.video_url && schemaProps.video_url.replace(/^\/videos\//, ''))
    || undefined
);

/**
 * True when a video/title replacement on an already-answered question is
 * about to happen — this editor only ever sees schema-side representations
 * (never hint-side `video_question`), so agreeing with the backend just
 * means resolving to the same value before comparing, as above.
 */
const isAnsweredVideoQuestionChanged = (oldSchemaProps = {}, newSchemaProps = {}) => (
    oldSchemaProps.title !== newSchemaProps.title
    || resolveSchemaVideoUrn(oldSchemaProps) !== resolveSchemaVideoUrn(newSchemaProps)
);

/* ------------------------------------------------------------------------
 * Composition — thin glue between the two, exported for the editor.
 * ---------------------------------------------------------------------- */

const convertFormDataToSchema = async (_formData = {}) => {
    const fields = (_formData.video_questions_section || []).map((field, i) => {
        const fieldId = field.field_id || `custom-${getUid()}`;
        const { schemaProps, hintProps } = videoQuestionToFieldDescriptor(field);
        return {
            fieldId,
            schemaProps,
            hintProps: { ...hintProps, order: (i + 1) * 10 },
        };
    });
    // No per-language titles here (that's the language-versions modal's job) —
    // omitting existingTranslations makes buildSchemaFromFields drop the key.
    return buildSchemaFromFields(fields);
};

const convertSchemaToFormData = (schema = {}) => {
    if (!schema?.schema) return {};
    return {
        video_questions_section: parseFieldsFromSchema(schema).map(({ fieldId, schemaProps, hintProps }) => ({
            ...fieldDescriptorToVideoQuestion(schemaProps, hintProps),
            field_id: fieldId,
        })),
    };
};

const getAnsweredQuestionsBeingReplaced = (originalForm, newForm) => {
    const originalHints = originalForm?.propertiesHints || {};
    const originalProperties = originalForm?.schema?.properties || {};
    const newProperties = newForm?.schema?.properties || {};
    return Object.keys(newProperties)
        .filter((fieldId) => originalProperties[fieldId])
        .map((fieldId) => ({
            fieldId,
            answeredCount: originalHints[fieldId]?.answered_count || 0,
            changed: isAnsweredVideoQuestionChanged(originalProperties[fieldId], newProperties[fieldId]),
        }))
        .filter((question) => question.changed && question.answeredCount > 0);
};

/**
 * Questions that are going away and that candidates have already answered.
 *
 * Disjoint from `getAnsweredQuestionsBeingReplaced` by construction: that one reads
 * fields present in BOTH documents, this one fields present only in the original.
 * The answer is not deleted when a question goes -- it stays in the field table and
 * simply stops being rendered -- which is exactly why the recruiter has to be told:
 * the count on the application drops with nothing on screen to explain it.
 */
const getAnsweredQuestionsBeingRemoved = (originalForm, newForm) => {
    const originalHints = originalForm?.propertiesHints || {};
    const originalProperties = originalForm?.schema?.properties || {};
    const newProperties = newForm?.schema?.properties || {};
    return Object.keys(originalProperties)
        .filter((fieldId) => !newProperties[fieldId])
        .map((fieldId) => ({
            fieldId,
            title: originalProperties[fieldId]?.title,
            answeredCount: originalHints[fieldId]?.answered_count || 0,
        }))
        .filter((question) => question.answeredCount > 0);
};

export {
    // composition (editor-facing)
    convertFormDataToSchema,
    convertSchemaToFormData,
    getAnsweredQuestionsBeingReplaced,
    getAnsweredQuestionsBeingRemoved,
    // generic
    buildSchemaFromFields,
    parseFieldsFromSchema,
    diffSchemaProperties,
    // video
    videoQuestionToFieldDescriptor,
    fieldDescriptorToVideoQuestion,
    isAnsweredVideoQuestionChanged,
};
